import { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { api as Api } from 'api/http';
import { CompanyContext } from 'contexts/CompanyContext';
import { AIContext } from 'contexts/AIContext';

/**
 * Custom hook for managing viral shorts
 * Provides state management and operations for AI-generated viral short videos
 */
const useViralShorts = () => {
    const { company } = useContext(CompanyContext);
    const { aiModels } = useContext(AIContext);
    
    // State management
    const [viralShorts, setViralShorts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [nextLink, setNextLink] = useState(null);
    const [pendingVideos, setPendingVideos] = useState(new Set());
    const [pollingVideos, setPollingVideos] = useState(new Set());
    const pollingTimeouts = useRef(new Map());
    
    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * Poll viral short video status
     * @param {string} videoId - The video ID to poll
     */
    const pollVideoStatus = useCallback(async (videoId) => {
        let attempts = 0;
        const maxAttempts = 120; // 10 minutes max (120 * 5 seconds)
        const pollInterval = 10000; // 10 seconds
        let isPolling = true;
        
        const poll = async () => {
            if (!isPolling || attempts >= maxAttempts) return;
            
            attempts++;
            
            try {
                const pollResponse = await Api.get(`/aiResults/${videoId}`);
                const { status } = pollResponse.data;
                
                if (status === 'completed' || status === 'failed') {
                    isPolling = false;
                    
                    // Clean up timeout reference
                    pollingTimeouts.current.delete(videoId);
                    
                    setPendingVideos(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                    
                    setPollingVideos(prev => {
                        const newSet = new Set(prev);
                        newSet.delete(videoId);
                        return newSet;
                    });
                    
                    // Refresh the list to show the completed video
                    fetchViralShorts(true);
                    return;
                }
                
                // Continue polling if still pending
                if (attempts < maxAttempts && isPolling) {
                    const timeoutId = setTimeout(poll, 5000);
                    pollingTimeouts.current.set(videoId, timeoutId);
                }
                
            } catch (err) {
                isPolling = false;
                console.error('Error polling viral short status:', err);
                
                // Clean up timeout reference
                pollingTimeouts.current.delete(videoId);
                
                // Remove from pending videos on error
                setPendingVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
                
                setPollingVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
            }
        };
        
        // Start the first poll after 10 seconds
        const timeoutId = setTimeout(poll, pollInterval);
        pollingTimeouts.current.set(videoId, timeoutId);
        
        // Set up cleanup for max attempts
        setTimeout(() => {
            if (attempts >= maxAttempts && isPolling) {
                isPolling = false;
                
                // Clean up timeout reference
                pollingTimeouts.current.delete(videoId);
                
                setPendingVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
                
                setPollingVideos(prev => {
                    const newSet = new Set(prev);
                    newSet.delete(videoId);
                    return newSet;
                });
            }
        }, maxAttempts * pollInterval);
    }, []);

    /**
     * Fetch viral shorts with pagination support
     * Filters by viral short models (models with isViralShortModel: true)
     * @param {boolean} replace - If true, replace existing videos, otherwise append
     * @returns {Promise<Object>} Response with data, meta, and links
     */
    const fetchViralShorts = useCallback(async (replace = true) => {
        if (!company?.id) {
            setViralShorts([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // Filter models that are marked as viral short models
            const viralShortModels = aiModels?.filter(m => m.isViralShortModel)?.map(m => m.model) || [];
            
            if (viralShortModels.length === 0) {
                console.warn('No viral short models found');
                setViralShorts([]);
                setLoading(false);
                return;
            }

            const url = !replace && nextLink 
                ? nextLink 
                : `/aiResults?include=videos,videos.thumbnail,user,user.image&sort=-created_at&filter[company_id]=${company?.id}&filter[type]=video&filter[model]=${viralShortModels.join(',')}&page[size]=10`;
            
            const response = await Api.get(url);
            
            if (replace) {
                setViralShorts(response.data);
            } else {
                setViralShorts(prev => [...prev, ...response.data]);
            }
            
            setNextLink(response.links?.next || null);
            
            // Check for pending videos and start polling for them
            const pending = response.data.filter(r => r.status === 'pending' || r.status === 'processing');
            if (pending.length > 0) {
                const newPendingIds = new Set([...pendingVideos, ...pending.map(p => p.id)]);
                setPendingVideos(newPendingIds);
                
                // Start polling for each pending video (avoid duplicate polling)
                pending.forEach(video => {
                    if (!pollingVideos.has(video.id)) {
                        setPollingVideos(prev => new Set([...prev, video.id]));
                        pollVideoStatus(video.id);
                    }
                });
            }

            return {
                data: response.data,
                meta: response.meta,
                links: response.links
            };
        } catch (err) {
            console.error('Error fetching viral shorts:', err);
            setError(err);
            throw err;
        } finally {
            setLoading(false);
        }
    }, [company?.id, nextLink, pendingVideos, pollingVideos, pollVideoStatus, aiModels]);

    // Auto-fetch viral shorts when company changes
    useEffect(() => {
        if (company?.id) {
            fetchViralShorts(true).catch(console.error);
        } else {
            setViralShorts([]);
        }
    }, [company?.id]);

    // Cleanup polling timeouts on unmount
    useEffect(() => {
        return () => {
            pollingTimeouts.current.forEach(timeoutId => {
                clearTimeout(timeoutId);
            });
            pollingTimeouts.current.clear();
        };
    }, []);

    return {
        // State
        viralShorts,
        loading,
        error,
        nextLink,
        pendingVideos,
        
        // Operations
        fetchViralShorts,
        clearError,
        
        // Computed values
        viralShortsCount: viralShorts.length,
        hasViralShorts: viralShorts.length > 0,
    };
};

export default useViralShorts;

